/**
 * Centralized precedence rule for fields whose value is influenced by both a
 * page-saved prop (typically AI-generated or owner-edited inside Puck) AND a
 * business-level setting (set in dashboard Settings).
 *
 * Before this helper existed, the precedence pattern was duplicated in five
 * component render() functions (Hero, CTA, Navigation, ContactForm, PricingInfo)
 * — every precedence tweak meant editing all five. See PRD-hydration-context-source
 * Phase 2 for the migration history.
 */

import { resolveCtaLabel } from "./cta-defaults";

interface BusinessBoundPropInput<T> {
  /** Value saved on the page (in Puck JSON). May be AI-generated or owner-edited. */
  savedValue: T | undefined | null;
  /** Resolved value from the business/site setting, with defaults already applied. */
  resolvedValue: T;
  /** True iff the business has an explicit owner-set value (not just the system default). */
  hasExplicitBusinessValue: boolean;
}

/**
 * Precedence (highest → lowest):
 *   1. Explicit business setting (owner picked it in Settings)
 *   2. Saved value on the page, if non-empty
 *   3. Resolved default (no owner choice, no saved content)
 *
 * Rationale: owners expect their Settings choice to win over whatever the AI
 * generated previously. Without (1), regen-rewritten pages keep showing stale
 * strings because the saved AI value is technically non-empty.
 */
const resolveBusinessBoundProp = <T>({
    savedValue,
    resolvedValue,
    hasExplicitBusinessValue,
  }: BusinessBoundPropInput<T>): T => {
    if (hasExplicitBusinessValue) {
      return resolvedValue;
    }
    if (isNonEmpty(savedValue)) {
      return savedValue as T;
    }
    return resolvedValue;
  },
  /**
   * Convenience wrapper for the most common business-bound prop: primary CTA text.
   *
   * Encapsulates the read of `business.ctaPrimaryLabel` and the call to
   * `resolveCtaLabel` so callers don't have to repeat both.
   */
  resolveBusinessCtaText = (
    savedValue: string | undefined | null,
    business: { ctaPrimaryLabel?: string | null } | undefined,
  ): string => resolveBusinessBoundProp({
    savedValue,
    resolvedValue: resolveCtaLabel(business),
    hasExplicitBusinessValue: Boolean(business?.ctaPrimaryLabel?.trim()),
  });

function isNonEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  return true;
}

export type { BusinessBoundPropInput };
export { resolveBusinessBoundProp, resolveBusinessCtaText };
